import { ChevronDownIcon } from '@heroicons/react/outline';

import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import {
  BUSINESS,
  BUSINESS_NUMBER,
  formatMoney,
  PHONE_DOCS_URL,
  PhoneGlyph,
  QUIET,
  TWILIO_US,
  useFormatLocale,
  useReceptionistT,
} from './kit';

/** Phone calls come in through the customer's own Twilio number. */
export default function ReceptionistSetup() {
  const { t } = useReceptionistT();
  const locale = useFormatLocale();
  const { ref, state } = useReveal<HTMLDivElement>(0.2);
  const numberPrice = formatMoney(TWILIO_US.localNumberPerMonth, 'usd', locale);

  const steps = [
    {
      title: t('Build your agent'),
      detail: t(
        'Add your website and documents, write a few instructions and test the answers in the Playground. This part is free.',
      ),
    },
    {
      title: t('Get a Twilio number'),
      detail: t(
        'Buy a voice number in Twilio, about {{price}} a month for a US local number, or use one you already have there.',
        { price: numberPrice },
      ),
    },
    {
      title: t('Switch on Twilio’s AI features'),
      detail: t(
        'In Twilio’s voice settings, enable the Predictive and Generative AI/ML Features Addendum. Without it, calls end as soon as they start.',
      ),
    },
    {
      title: t('Connect the number to your agent'),
      detail: t(
        'Paste your Account SID, Auth Token and number, write a greeting and choose a language. We point the number at your agent for you.',
      ),
    },
    {
      title: t('Call it'),
      detail: t('Dial your number and talk to your receptionist.'),
    },
  ];

  return (
    <section id='setup' className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:items-start lg:gap-12 lg:px-8'>
        <div className='lg:col-span-5'>
          <SectionHeading
            size='md'
            title={t('Your number, your Twilio account.')}
            tail={t('We do the wiring.')}
            lead={t(
              'Calls come in through a phone number in your own Twilio account. Twilio bills the number and the minutes, and you set up everything else in your agent.',
            )}
          />

          <ol className='relative mt-10 space-y-6'>
            <span
              aria-hidden='true'
              className='absolute bottom-4 start-[15px] top-4 w-px bg-[#0f0f28]/10 dark:bg-white/10'
            />
            {steps.map(({ title, detail }, index) => (
              <li key={title} className='relative flex gap-4'>
                <span
                  className={clsxm(
                    'relative flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-semibold tabular-nums',
                    index === steps.length - 1
                      ? 'bg-primary-600 text-white'
                      : clsxm(tone.surface, tone.line, tone.ink),
                  )}
                >
                  {index + 1}
                </span>
                <span className='pt-1'>
                  <span
                    className={clsxm(
                      'block text-[15px] font-semibold',
                      tone.ink,
                    )}
                  >
                    {title}
                  </span>
                  <span
                    className={clsxm(
                      'mt-1 block text-[15px] leading-7',
                      tone.body,
                    )}
                  >
                    {detail}
                  </span>
                </span>
              </li>
            ))}
          </ol>

          <div className='mt-10 flex flex-wrap items-center gap-x-6 gap-y-3'>
            <a
              href={PHONE_DOCS_URL}
              className='group inline-flex items-center gap-2 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
            >
              {t('Read the setup guide')}
              <ArrowGlyph className='transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
            </a>
          </div>
          <p className={clsxm('mt-4 max-w-md text-sm leading-6', QUIET)}>
            {t(
              'Already have a business number? Keep it: forward its calls to your Twilio number, or move the number over to Twilio.',
            )}
          </p>
        </div>

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(
            agents.reveal,
            'min-w-0 lg:sticky lg:top-24 lg:col-span-7',
          )}
        >
          <Stage
            variant='lavender'
            halftoneAt='100% 0%'
            className='px-4 py-10 sm:px-12 sm:py-14'
          >
            <p className='sr-only'>
              {t(
                'Example: the phone settings of an agent, with the Twilio account details, the number, a greeting and the language filled in, and the number connected.',
              )}
            </p>
            <div aria-hidden='true' className='relative mx-auto max-w-[27rem]'>
              <div className={agents.rv} style={delay(0)}>
                <ConnectForm />
              </div>
              <div
                className={clsxm(
                  agents.rv,
                  'relative -mt-5 ms-auto w-[88%] sm:-me-8 sm:w-[17.5rem]',
                )}
                style={delay(450)}
              >
                <ConnectedToast />
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </section>
  );
}

/**
 * The number's language in the mock, matching the page: Twilio's codes for
 * the languages it lists a default voice for. Chinese, Arabic and Malay
 * aren't in that list (the phone channel can't pick a voice), so their
 * pages show an English greeting on en-US instead of a setup that fails.
 */
const CALL_LANGUAGE: Record<string, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  de: 'de-DE',
  ko: 'ko-KR',
  th: 'th-TH',
  pt: 'pt-BR',
  vi: 'vi-VN',
};

function ConnectForm() {
  const { t, lang } = useReceptionistT();
  const language = CALL_LANGUAGE[lang];
  const fields: {
    label: string;
    value: string;
    mono?: boolean;
    ltr?: boolean;
  }[] = [
    { label: 'Account SID', value: 'AC7d3c…e91f', mono: true, ltr: true },
    { label: 'Auth Token', value: '••••••••••••••••', mono: true, ltr: true },
    {
      label: t('Twilio phone number'),
      value: BUSINESS_NUMBER,
      ltr: true,
    },
    {
      label: t('Welcome greeting'),
      value: language
        ? t('Thanks for calling {{business}}. How can I help?', {
            business: BUSINESS,
          })
        : `Thanks for calling ${BUSINESS}. How can I help?`,
      ltr: !language,
    },
  ];

  return (
    <div
      className={clsxm(
        'overflow-hidden rounded-[22px] shadow-[0_40px_90px_-35px_rgba(30,20,90,0.45)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#f22f46] text-white'>
          <PhoneGlyph className='h-[18px] w-[18px]' />
        </span>
        <p
          className={clsxm(
            'min-w-0 flex-1 text-[15px] font-semibold leading-tight',
            tone.ink,
          )}
        >
          {t('Phone calls (Twilio)')}
        </p>
        <span className='flex flex-none items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300'>
          <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
          {t('Connected')}
        </span>
      </div>

      <div className='space-y-4 px-5 py-5'>
        {fields.map(({ label, value, mono, ltr }) => (
          <div key={label}>
            <p className={clsxm('text-xs font-medium', QUIET)}>{label}</p>
            <p
              className={clsxm(
                'mt-1.5 rounded-xl bg-[#f6f6f9] px-3.5 py-2.5 text-[14px] leading-snug ring-1 ring-inset ring-black/[0.05] dark:bg-white/[0.04] dark:ring-white/[0.08]',
                mono && 'truncate',
                mono && 'font-mono text-[13px] tracking-tight',
                tone.ink,
              )}
            >
              <span dir={ltr ? 'ltr' : undefined}>{value}</span>
            </p>
          </div>
        ))}
        <div>
          <p className={clsxm('text-xs font-medium', QUIET)}>{t('Language')}</p>
          <p
            className={clsxm(
              'mt-1.5 flex items-center justify-between rounded-xl bg-[#f6f6f9] px-3.5 py-2.5 text-[14px] ring-1 ring-inset ring-black/[0.05] dark:bg-white/[0.04] dark:ring-white/[0.08]',
              tone.ink,
            )}
          >
            <span dir='ltr' className='font-mono text-[13px]'>
              {language ?? 'en-US'}
            </span>
            <ChevronDownIcon className={clsxm('h-4 w-4', QUIET)} />
          </p>
        </div>
      </div>

      <div className='flex items-center justify-between gap-3 border-t border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span
          className={clsxm('flex min-w-0 items-center gap-1.5 text-xs', QUIET)}
        >
          <CheckGlyph className='h-3.5 w-3.5 text-emerald-500' />
          <span className='truncate'>{t('Call routing set up for you')}</span>
        </span>
        <span className='flex-none rounded-full bg-primary-600 px-4 py-2 text-[13px] font-semibold text-white'>
          {t('Save')}
        </span>
      </div>
    </div>
  );
}

function ConnectedToast() {
  const { t } = useReceptionistT();

  return (
    <div className='flex items-center gap-3 rounded-2xl bg-[#0d0c1f] px-4 py-3.5 text-white shadow-[0_30px_60px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10'>
      <span className='relative flex h-9 w-9 flex-none items-center justify-center rounded-full bg-emerald-500'>
        <PhoneGlyph className='h-4 w-4' />
      </span>
      <span className='min-w-0'>
        <span className='block truncate text-[13px] font-semibold'>
          {t('Your receptionist is live')}
        </span>
        <span className='block truncate text-xs text-white/60'>
          <span dir='ltr'>{BUSINESS_NUMBER}</span>
        </span>
      </span>
    </div>
  );
}
